import Dropdown from '@/components/Dropdown'
import {
  MoreHorizontal,
  Trash,
  Edit2,
  Folder,
  FolderPlus,
  FolderMinus,
  Share,
  Check,
  X,
  Archive,
  Inbox,
  Tag,
} from 'react-feather'
import analytics from '@/utils/analytics'
import api from '@/utils/api'
import { useRecoilState } from 'recoil'
import {
  modalState,
  playlistsState,
  deleteVideo,
  updateVideo,
  createItem,
  addVideoToPlaylist,
  deleteItem,
} from '@/store'
import toast from 'react-hot-toast'
import { useState } from 'react'
import classNames from 'classnames'
import { copyValueToClipBoard } from '@/utils/index'
import Input from '@/components/Input'

export default function VideoOptions({
  className = '',
  video,
  setVideos,
  setPlaylists,
}) {
  const [, setModal] = useRecoilState(modalState)

  function updateTitle(title) {
    try {
      analytics.track('Updating video title')

      const updatedVideo = {
        ...video,
        title,
      }

      updateVideo({ video: updatedVideo, setVideos, setPlaylists })

      api.put(`/videos/${video._id}`, {
        title,
      })
    } catch (error) {
      toast.error('Something went wrong')
    }
  }

  async function updateTags(tags) {
    try {
      analytics.track('Updating video tags')

      const updatedVideo = {
        ...video,
        tags,
      }

      updateVideo({ video: updatedVideo, setVideos, setPlaylists })

      const tagsWithIds = []

      for (let i = 0; i < tags.length; i++) {
        const tag = tags[i]

        if (tag._id) {
          tagsWithIds.push(tag)
        } else {
          const response = await api.post('/tags', tag)
          const createdTag = response.data.data
          tagsWithIds.push(createdTag)
        }
      }

      api.put(`/videos/${video._id}`, {
        tags: tagsWithIds.map((tag) => tag._id),
      })
    } catch (error) {
      toast.error('Something went wrong')
    }
  }

  function unarchiveVideo() {
    try {
      analytics.track('Archiving video')

      const updatedVideo = {
        ...video,
        archived: false,
      }

      updateVideo({ video: updatedVideo, setVideos, setPlaylists })

      api.put(`/videos/${video._id}`, {
        archived: false,
      })
    } catch (error) {
      toast.error('Something went wrong')
    }
  }

  function archiveVideo() {
    try {
      analytics.track('Archiving video')

      const updatedVideo = {
        ...video,
        archived: true,
      }

      updateVideo({ video: updatedVideo, setVideos, setPlaylists })

      api.put(`/videos/${video._id}`, {
        archived: true,
      })
    } catch (error) {
      toast.error('Something went wrong')
    }
  }

  async function onVideoRemove() {
    analytics.track('Deleting video')
    deleteVideo({ video, setPlaylists, setVideos })
    api.delete(`/videos/${video._id}`)
  }

  function removeVideoFromPlaylist() {
    analytics.track('Removing video from playlist')

    api.put(`/videos/${video._id}`, {
      playlist: null,
    })

    setPlaylists((prev) =>
      prev.map((playlist) => {
        if (playlist._id === video.playlist) {
          return {
            ...playlist,
            videos: playlist.videos.filter(
              (_video) => _video._id !== video._id
            ),
          }
        } else {
          return playlist
        }
      })
    )

    setVideos((prev) =>
      createItem({
        items: prev,
        item: {
          ...video,
          playlist: null,
        },
      })
    )
  }

  function markAsPlayed() {
    analytics.track('Marking video as finished')

    api.put(`/videos/${video._id}`, {
      finished: true,
      lastProgress: video.duration,
    })

    const updatedVideo = {
      ...video,
      finished: true,
      lastProgress: video.duration,
    }

    updateVideo({ video: updatedVideo, setVideos, setPlaylists })
  }

  function markAsUnplayed() {
    analytics.track('Marking video as unfinished')

    api.put(`/videos/${video._id}`, {
      finished: false,
      lastProgress: 0,
    })

    const updatedVideo = {
      ...video,
      finished: false,
      lastProgress: 0,
    }

    updateVideo({ video: updatedVideo, setVideos, setPlaylists })
  }

  async function cloneVideo() {
    const response = await toast.promise(
      api.post(`/videos/${video._id}/clone`),
      {
        loading: 'Cloning...',
        success: 'Video cloned',
        error: (err) => err.toString(),
      }
    )

    const newVideo = response.data.data

    setVideos((prevVideos) => [...prevVideos, newVideo])
  }

  function handleMenuClick(key) {
    analytics.track('Video option clicked', { key })

    switch (key) {
      case 'edit-title':
        setModal({
          isEdit: true,
          title: 'Update Title',
          value: video.title,
          onClick: (e, newTitle) => updateTitle(newTitle),
        })
        break

      case 'edit-tags':
        setModal({
          isTags: true,
          type: 'video',
          title: 'Edit Video Tags',
          tags: video.tags,
          onClick: (e, newTags) => updateTags(newTags),
        })
        break

      case 'share':
        copyValueToClipBoard(
          `${window.document.location.origin}/watch/${video._id}`
        )
        analytics.track('Public link copied')
        toast.success('Public link copied')
        break

      case 'move-to-playlist':
        setModal({
          title: 'Move to Playlist',
          body: (
            <MoveToPlaylistModalBody
              {...{ setModal, setVideos, setPlaylists, video }}
            />
          ),
        })
        break

      case 'remove-from-playlist':
        setModal({
          isConfirm: true,
          confirmLabel: 'Remove',
          title: 'Remove from Playlist',
          body: 'Are you sure you want to remove this video from the playlist?',
          onClick: () => removeVideoFromPlaylist(),
        })
        break

      case 'mark-played':
        markAsPlayed()
        break

      case 'mark-unplayed':
        markAsUnplayed()
        break

      case 'clone':
        cloneVideo()
        break

      case 'archive':
        archiveVideo()
        break

      case 'unarchive':
        unarchiveVideo()
        break

      case 'delete':
        setModal({
          isConfirm: true,
          title: 'Delete Video',
          body: 'Are you sure you want to delete this video?',
          onClick: onVideoRemove,
        })
        break

      default:
        break
    }
  }

  const options = [
    {
      name: 'Edit Title',
      value: 'edit-title',
      icon: (
        <Edit2 className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
      ),
    },
    {
      name: 'Edit tags',
      value: 'edit-tags',
      icon: (
        <Tag className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
      ),
    },
    {
      name: 'Copy link to share',
      value: 'share',
      icon: (
        <Share className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
      ),
    },
    {
      name: 'Move to Playlist',
      value: 'move-to-playlist',
      icon: (
        <Folder className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
      ),
    },
    ...(video.playlist
      ? [
          {
            name: 'Remove from Playlist',
            value: 'remove-from-playlist',
            icon: (
              <FolderMinus className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
            ),
          },
        ]
      : []),
    ...(video.finished
      ? [
          {
            name: 'Mark as Unplayed',
            value: 'mark-unplayed',
            icon: (
              <X className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
            ),
          },
        ]
      : [
          {
            name: 'Mark as Played',
            value: 'mark-played',
            icon: (
              <Check className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
            ),
          },
        ]),
    ...(video.archived
      ? [
          {
            name: 'Unarchive',
            value: 'unarchive',
            icon: (
              <Inbox className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
            ),
          },
        ]
      : [
          {
            name: 'Archive',
            value: 'archive',
            icon: (
              <Archive className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
            ),
          },
        ]),
    // {
    //   name: 'Clone',
    //   value: 'clone',
    //   icon: (
    //     <Copy className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
    //   ),
    // },
    {
      name: 'Delete',
      value: 'delete',
      icon: (
        <Trash className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500" />
      ),
    },
  ]

  return (
    <Dropdown
      options={options}
      onChange={handleMenuClick}
      className={className}
    >
      <MoreHorizontal
        className="text-primary"
        style={{ width: '20px', minWidth: '20px' }}
      />
    </Dropdown>
  )
}

function MoveToPlaylistModalBody({ setModal, setVideos, setPlaylists, video }) {
  const [newPlaylistName, setNewPlaylistName] = useState('')
  const [selectedPlaylist, setSelectedPlaylist] = useState(video.playlist)
  const [playlists] = useRecoilState(playlistsState)

  function onCreate() {
    if (selectedPlaylist) {
      addToPlaylist()
    } else {
      createPlaylist()
    }
  }

  function addToPlaylist(createdPlaylistId) {
    try {
      analytics.track('Adding video to playlist')

      const playlistId = createdPlaylistId || selectedPlaylist

      api.put(`/videos/${video._id}`, {
        playlist: playlistId,
      })

      if (!createdPlaylistId) {
        addVideoToPlaylist({ video, playlistId, setPlaylists, setVideos })
      }
    } catch (error) {
      toast.error('Something went wrong')
    }

    setModal(null)
  }

  function onPlaylistCreated(response) {
    const newPlaylist = response.data.data
    setPlaylists((prev) =>
      createItem({
        items: prev,
        item: {
          ...newPlaylist,
          videos: [video],
        },
      })
    )

    setVideos((prev) =>
      deleteItem({
        items: prev,
        itemID: video._id,
      })
    )

    addToPlaylist(newPlaylist._id)
  }

  function createPlaylist() {
    toast.promise(
      api
        .post('/playlists', { title: newPlaylistName })
        .then(onPlaylistCreated),
      {
        loading: 'Creating...',
        success: 'Playlist created',
        error: 'Could not save',
      }
    )
  }

  function onPlaylistNameChange(name) {
    setSelectedPlaylist(null)
    setNewPlaylistName(name)
  }

  const isCreateButtonDisabled = !selectedPlaylist && !newPlaylistName

  return (
    <div className="w-full">
      {!playlists.length && (
        <span className="text-center w-full text-lg">
          Please, create a playlist first
        </span>
      )}

      {playlists
        .filter(({ _id }) => _id !== video.playlist)
        .map((playlist) => {
          const playlistId = playlist._id
          const isSelected = selectedPlaylist === playlistId

          const className = classNames(
            'shadow-md flex mb-3 flex-col p-5 rounded-lg cursor-pointer bg-white w-30 border-2 border-gray-100 transition hover:border-primary',
            { 'border-primary': isSelected }
          )

          return (
            <div
              key={playlistId}
              onClick={() => setSelectedPlaylist(playlistId)}
              className={className}
            >
              <div className="flex items-center font-medium text-black">
                <Folder className="h-5 w-5 mr-2" />
                <h3 className="truncate" title={playlist.title}>
                  {playlist.title}
                </h3>
                <div className="ml-auto mr-1 rounded-full center bg-gray-300 box-content text-black w-auto p-2 h-2 text-sm">
                  {playlist.videos.length}
                </div>
              </div>
            </div>
          )
        })}

      <div className="shadow-md flex mb-3 flex-col p-5 rounded-lg bg-white w-30 border-2 border-gray-100">
        <div className="flex items-center font-medium text-black">
          <FolderPlus className="h-5 w-5 mr-2" />
          <Input
            type="text"
            name="new-playlist"
            id="new-playlist"
            placeholder="New Playlist"
            className="border-non"
            onChange={({ target }) => onPlaylistNameChange(target.value)}
          />
        </div>
      </div>

      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          className={`clickable w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white clickable-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm ${
            isCreateButtonDisabled
              ? 'opacity-50 pointer-events-none cursor-not-allowed'
              : ''
          }`}
          onClick={onCreate}
          disabled={isCreateButtonDisabled}
        >
          Accept
        </button>
        <button
          type="button"
          className="clickable mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
          onClick={() => setModal(null)}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
